import React, { useState, useEffect, useCallback } from 'react';
import { Workflow, Plus, Edit2, Trash2, X, Search, Loader2, Link2, Play, ChevronRight, HelpCircle, ChevronDown } from 'lucide-react';
import { reconRulesApi } from '../../services/api';
import DataTable from '../../components/DataTable';
import { mockProcesses, mockRulesets } from './mockData';

const DIRECTIONS = ['inbound', 'outbound'];
const PARTNER_TYPES = ['client', 'vendor', 'partner'];
const FILE_TYPES = ['eligibility', 'task_completion', 'card_order', 'value_load', 'card_ship', 'other'];
const SCHEDULES = ['daily', 'weekly', 'monthly', 'ad-hoc'];

const HowTo = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-blue-800">
          <HelpCircle className="w-4 h-4" /> How to use Processes
        </span>
        <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-blue-900 space-y-3">
          <div>
            <p className="font-semibold mb-1">What are Processes?</p>
            <p>Processes represent file processing jobs for specific clients, vendors, or partners. Each process defines the file direction (inbound/outbound), partner, file type, and the Redshift schema/table where the data lands.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Steps</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li><strong>Create a Process</strong> - Click "Add Process" and fill in the details. Example: "KP Eligibility Inbound" with direction=inbound, partner=KP, file_type=eligibility.</li>
              <li><strong>Set Source</strong> - Specify the Redshift schema and table where the file data is loaded (e.g., <code className="bg-blue-100 px-1 rounded">etl_core.ref_consumer</code>).</li>
              <li><strong>Attach Rulesets</strong> - Click "Rulesets" to attach one or more rulesets. Each ruleset contains the rules that will validate this process.</li>
              <li><strong>Run</strong> - Click the green "Run" button to execute all attached rulesets against the process. Results appear in the Execution Dashboard.</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold mb-1">Example Processes</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Eligibility file from KP (inbound, client)</li>
              <li>Task Completion file from HAP (inbound, client)</li>
              <li>Card Order file to FIS (outbound, vendor)</li>
              <li>Value Load file to FIS (outbound, vendor)</li>
              <li>Card Ship file from AB Corp (inbound, partner)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ReconProcesses({ onNavigate }) {
  const [processes, setProcesses] = useState([]);
  const [allRulesets, setAllRulesets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRulesetsPanel, setShowRulesetsPanel] = useState(null);
  const [editingProcess, setEditingProcess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(null);
  const [form, setForm] = useState({
    process_code: '', process_name: '', description: '', direction: 'inbound',
    partner_type: 'client', partner_name: '', file_type: 'eligibility',
    source_schema: '', source_table: '', expected_schedule: 'daily', is_active: true,
  });
  const [selectedRulesets, setSelectedRulesets] = useState([]);

  const loadData = useCallback(async () => {
    try {
      let pData, rsData;
      try {
        const [procRes, rsRes] = await Promise.all([
          reconRulesApi.listProcesses(),
          reconRulesApi.listRulesets(),
        ]);
        pData = procRes.data;
        rsData = rsRes.data;
      } catch { pData = []; rsData = []; }
      setProcesses(pData && pData.length > 0 ? pData : mockProcesses);
      setAllRulesets((rsData && rsData.length > 0 ? rsData : mockRulesets).filter(rs => rs.is_active));
    } catch (err) {
      console.error('Failed to load processes:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = processes.filter(p => {
    if (filterDirection && p.direction !== filterDirection) return false;
    if (!search) return true;
    return p.process_code?.toLowerCase().includes(search.toLowerCase()) ||
      p.process_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.partner_name?.toLowerCase().includes(search.toLowerCase());
  });

  const openCreate = () => {
    setEditingProcess(null);
    setForm({
      process_code: '', process_name: '', description: '', direction: 'inbound',
      partner_type: 'client', partner_name: '', file_type: 'eligibility',
      source_schema: '', source_table: '', expected_schedule: 'daily', is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (proc) => {
    setEditingProcess(proc);
    setForm({
      process_code: proc.process_code, process_name: proc.process_name, description: proc.description || '',
      direction: proc.direction, partner_type: proc.partner_type, partner_name: proc.partner_name,
      file_type: proc.file_type, source_schema: proc.source_schema || '', source_table: proc.source_table || '',
      expected_schedule: proc.expected_schedule || 'daily', is_active: proc.is_active,
    });
    setShowModal(true);
  };

  const openRulesetsPanel = async (proc) => {
    try {
      const res = await reconRulesApi.getProcess(proc.process_id);
      const existing = (res.data.rulesets || []).map((rs, i) => ({
        ruleset_id: rs.ruleset_id, sort_order: rs.sort_order || i, is_active: rs.is_active !== false,
      }));
      setSelectedRulesets(existing);
      setShowRulesetsPanel(proc);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingProcess) {
        await reconRulesApi.updateProcess(editingProcess.process_id, form);
      } else {
        await reconRulesApi.createProcess(form);
      }
      setShowModal(false);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (proc) => {
    if (!confirm(`Deactivate process "${proc.process_name}"?`)) return;
    try {
      await reconRulesApi.deleteProcess(proc.process_id);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleRuleset = (rulesetId) => {
    setSelectedRulesets(prev => {
      const exists = prev.find(r => r.ruleset_id === rulesetId);
      if (exists) return prev.filter(r => r.ruleset_id !== rulesetId);
      return [...prev, { ruleset_id: rulesetId, sort_order: prev.length, is_active: true }];
    });
  };

  const saveRulesetAssignment = async () => {
    setSaving(true);
    try {
      await reconRulesApi.updateProcessRulesets(showRulesetsPanel.process_id, selectedRulesets);
      setShowRulesetsPanel(null);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExecute = async (proc) => {
    if (!confirm(`Run reconciliation for "${proc.process_name}"?`)) return;
    setExecuting(proc.process_id);
    try {
      const res = await reconRulesApi.execute(proc.process_id);
      const data = res.data;
      const passed = data.results?.filter(r => r.status === 'pass').length || 0;
      const failed = data.results?.filter(r => ['fail', 'error'].includes(r.status)).length || 0;
      alert(`Reconciliation complete!\nRun ID: ${data.run_id}\nPassed: ${passed}, Failed: ${failed}`);
    } catch (err) {
      alert('Execution failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setExecuting(null);
    }
  };

  const columns = [
    { key: 'process_code', label: 'Code' },
    { key: 'process_name', label: 'Name' },
    { key: 'partner_name', label: 'Partner' },
    { key: 'direction', label: 'Direction', render: (val) =>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${val === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
        {val}
      </span>
    },
    { key: 'file_type', label: 'File Type' },
    { key: 'ruleset_count', label: 'Rulesets', format: 'number' },
    { key: 'expected_schedule', label: 'Schedule' },
    { key: 'is_active', label: 'Active', render: (val) =>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
        {val ? 'Active' : 'Inactive'}
      </span>
    },
    { key: 'actions', label: 'Actions', sortable: false, render: (_, row) => (
      <div className="flex gap-1">
        <button onClick={() => handleExecute(row)} disabled={executing === row.process_id}
          className="text-green-600 hover:text-green-800 text-xs px-2 py-1 bg-green-50 rounded flex items-center gap-1">
          {executing === row.process_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run
        </button>
        <button onClick={() => openRulesetsPanel(row)} className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 bg-blue-50 rounded flex items-center gap-1">
          <Link2 className="w-3 h-3" /> Rulesets
        </button>
        <button onClick={() => openEdit(row)} className="text-amber-600 hover:text-amber-800"><Edit2 className="w-4 h-4" /></button>
        <button onClick={() => handleDelete(row)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Workflow className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">File Processes</h1>
            <p className="text-sm text-gray-500">{processes.length} processes configured</p>
          </div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors">
          <Plus className="w-4 h-4" /> Add Process
        </button>
      </div>

      <HowTo />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search processes..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent" />
        </div>
        <select value={filterDirection} onChange={e => setFilterDirection(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
          <option value="">All Directions</option>
          {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* Table */}
      <DataTable data={filtered} columns={columns} loading={loading}
        groupBy="partner_name" groupLabelKey="partner_name"
        emptyMessage="No processes found. Create your first process to get started." />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">{editingProcess ? 'Edit Process' : 'Create Process'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Process Code *</label>
                  <input type="text" value={form.process_code} disabled={!!editingProcess}
                    onChange={e => setForm({...form, process_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 disabled:bg-gray-100"
                    placeholder="e.g. kp_eligibility_inbound" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Process Name *</label>
                  <input type="text" value={form.process_name}
                    onChange={e => setForm({...form, process_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. KP Eligibility Inbound" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400" rows={2} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Direction *</label>
                  <select value={form.direction} onChange={e => setForm({...form, direction: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner Type *</label>
                  <select value={form.partner_type} onChange={e => setForm({...form, partner_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {PARTNER_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner Name *</label>
                  <input type="text" value={form.partner_name}
                    onChange={e => setForm({...form, partner_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. KP, FIS, AB Corp" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File Type *</label>
                  <select value={form.file_type} onChange={e => setForm({...form, file_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {FILE_TYPES.map(ft => <option key={ft} value={ft}>{ft}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Schema</label>
                  <input type="text" value={form.source_schema}
                    onChange={e => setForm({...form, source_schema: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. etl_core" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Table</label>
                  <input type="text" value={form.source_table}
                    onChange={e => setForm({...form, source_table: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. ref_consumer" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                  <select value={form.expected_schedule} onChange={e => setForm({...form, expected_schedule: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
                    {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
                    <label className="text-sm text-gray-700">Active</label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave}
                disabled={saving || !form.process_code || !form.process_name || !form.partner_name}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingProcess ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rulesets Assignment Panel */}
      {showRulesetsPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-lg font-semibold">Attach Rulesets</h2>
                <p className="text-sm text-gray-500">{showRulesetsPanel.process_name}</p>
              </div>
              <button onClick={() => setShowRulesetsPanel(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-600 mb-4">{selectedRulesets.length} rulesets attached.</p>
              {allRulesets.map(rs => {
                const isSelected = selectedRulesets.some(s => s.ruleset_id === rs.ruleset_id);
                return (
                  <div key={rs.ruleset_id}
                    onClick={() => toggleRuleset(rs.ruleset_id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                      {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-sm">{rs.ruleset_name}</span>
                      <span className="ml-2 text-xs text-gray-500">{rs.rule_count} rules</span>
                      {rs.description && <p className="text-xs text-gray-400 mt-0.5">{rs.description}</p>}
                    </div>
                  </div>
                );
              })}
              {allRulesets.length === 0 && <p className="text-gray-500 text-sm">No active rulesets available.</p>}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowRulesetsPanel(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={saveRulesetAssignment} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save ({selectedRulesets.length} rulesets)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
